import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { InquiryPage } from './InquiryPage.js'
import { NoAccessPage } from './NoAccessPage.js'
import { useAccessState } from './useAccessState.js'

/**
 * Never shows a state page on a failed load: a false "not assigned" would invite a
 * needless inquiry.
 */
export function AccessGate({ children }: { children: ReactNode }) {
  const { data, loading, error, reload, setData } = useAccessState()

  if (loading) {
    return <main className="mx-auto max-w-[1180px] px-8 py-10 text-sm text-subtle">Loading…</main>
  }
  if (!data) {
    return (
      <main className="mx-auto flex max-w-[1180px] flex-wrap items-center gap-4 px-8 py-10">
        <p className="text-sm text-danger">Couldn’t check your access. Please try again.</p>
        <Button variant="primaryOutline" size="sm" onClick={reload}>
          Retry
        </Button>
        {error && <span className="sr-only">{error}</span>}
      </main>
    )
  }
  switch (data.state) {
    case 'not_assigned':
      return (
        <InquiryPage
          access={data}
          onSubmitted={(inquiry) => setData({ ...data, inquiry, canSubmitInquiry: false })}
        />
      )
    case 'no_access':
      return <NoAccessPage contact={data.contact} />
    default:
      return <>{children}</>
  }
}
